import { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectIsAuthenticated, selectUser } from '@/store/slices/authSlice';
import {
  setActiveTenant,
  setTenants,
  setTenantResolutionLoading,
  setTenantResolutionError,
  setBrandTenant,
  setModules,
  clearTenant,
} from '@/store/slices/tenantSlice';
import type { TenantInfo } from '@/store/slices/tenantSlice';
import { useGetClientTenantsQuery } from '@/store/api/endpoints/client/userProfileApi';
import { useGetClientTenantQuery } from '@/store/api/endpoints/client/tenantsApi';
import { normalizeTenantCode, parseEntryUrl } from '@/lib/auth/authFlow';

interface ClientTenantSyncProviderProps {
  children: React.ReactNode;
}

/**
 * Resolves and validates the tenant in the signed Auth0 `tenant_code` claim.
 *
 * The URL and localStorage are never used to choose an active tenant. The URL
 * is inspected only to pause old-token queries while a tenant switch is in
 * progress. AuthenticationController obtains an organization-scoped token;
 * this provider verifies the effective tenant through the bearer-scoped
 * GET /tenants endpoint and uses GET /profile/tenants only to populate the
 * tenant picker. This works for both regular membership and management
 * "login as tenant" sessions.
 */
export function ClientTenantSyncProvider({ children }: ClientTenantSyncProviderProps) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const user = useAppSelector(selectUser);
  const tokenTenantCode = normalizeTenantCode(user?.tenantCode);
  const entry = parseEntryUrl(window.location);
  const urlTenantMatchesToken =
    !entry.requestedTenantCode || entry.requestedTenantCode === tokenTenantCode;
  const shouldResolveTenant =
    isAuthenticated &&
    !!tokenTenantCode &&
    !entry.isLogin &&
    !entry.isAuthCallback &&
    urlTenantMatchesToken;

  const { data: tenantsData } = useGetClientTenantsQuery(undefined, {
    skip: !shouldResolveTenant,
  });

  // This endpoint is scoped by the bearer token. For management impersonation
  // it is also the authoritative proof that the API accepted the requested
  // effective tenant.
  const {
    data: tenantDetail,
    isLoading: tenantDetailLoading,
    isError: tenantDetailError,
    error: tenantDetailQueryError,
  } = useGetClientTenantQuery(undefined, {
    skip: !shouldResolveTenant,
  });

  useEffect(() => {
    if (!shouldResolveTenant) return;

    if (tenantDetailLoading) {
      dispatch(setTenantResolutionLoading());
      return;
    }

    if (tenantDetailError) {
      const status =
        tenantDetailQueryError && 'status' in tenantDetailQueryError
          ? String(tenantDetailQueryError.status)
          : null;
      dispatch(
        setTenantResolutionError(
          status
            ? `Tenant resolution request failed (${status}).`
            : 'Tenant resolution request failed.',
        ),
      );
      dispatch(setActiveTenant(null));
      return;
    }

    if (!tenantDetail) return;

    const detailCode = normalizeTenantCode(tenantDetail.code);
    if (detailCode !== tokenTenantCode) {
      dispatch(setTenantResolutionError('The API returned a different effective tenant.'));
      dispatch(setActiveTenant(null));
      return;
    }

    const effectiveTenant: TenantInfo = {
      tenantId: tenantDetail.id,
      name: tenantDetail.name,
      code: detailCode,
    };
    const memberships: TenantInfo[] = (tenantsData?.items ?? []).map((tenant) => ({
      tenantId: tenant.tenantId,
      name: tenant.name,
      code: normalizeTenantCode(tenant.code) ?? tenant.code,
    }));
    const availableTenants = [
      effectiveTenant,
      ...memberships.filter(
        (tenant) => normalizeTenantCode(tenant.code) !== detailCode,
      ),
    ];

    dispatch(setTenants(availableTenants));
    dispatch(setBrandTenant(null));
    dispatch(
      setActiveTenant({
        tenant: effectiveTenant,
        userSub: user?.sub,
      }),
    );
  }, [
    shouldResolveTenant,
    tenantsData,
    tenantDetail,
    tenantDetailLoading,
    tenantDetailError,
    tenantDetailQueryError,
    tokenTenantCode,
    user?.sub,
    dispatch,
  ]);

  useEffect(() => {
    if (tenantDetail?.modules) {
      dispatch(setModules(tenantDetail.modules));
    }
  }, [tenantDetail, dispatch]);

  useEffect(() => {
    if (!isAuthenticated) {
      dispatch(clearTenant());
    }
  }, [isAuthenticated, dispatch]);

  return <>{children}</>;
}
